import React, { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ProtectedRoute from "./ProtectedRoute";
import { Login } from "../Pages/Login";
import { Signup } from "../Pages/Signup";
import { ForgotPassword } from "../Pages/ForgotPassword";
import { EmailVerification } from "../Pages/EmailVerification";
import { CompleteKYC } from "../Pages/CompleteKYC";
import { KYCAuth } from "../Pages/KYCAuth";
import { PageNotFound } from "../Pages/PageNotFound";
import { DepositBank } from "../Pages/DepositBank";
import { Withdrawal } from "../Pages/Withdrawal";
import { WithdrawalBank } from "../Pages/WithdrawalBank";
import { NGeniusCallback } from "../Pages/NGeniusCallback";
import { CryptoCallback } from "../Pages/CryptoCallback";
import { StripePaymentResult } from "../Pages/StripePaymentResult";
import { Dashboard } from "../Pages/Dashboard";
import { MainPage } from "../Pages/MainPage";
import { getLocalStorageItem } from "../Utils/Helper";
import AccountOverview from "../Components/AccountSection/AccountOverview";
import TradingOverview from "../Components/AccountSection/TradingOverview";
import SignupRoute from "../Components/CorporateSignup/SignupRoute";

export const AllRoutes = () => {
  const isAuthenticated = useSelector((state) => state?.Login?.isAuthenticated);
  const kycStatus = useSelector((state) => state?.Login?.userData?.kyc_status);
  const emailVerificationStatus = useSelector(
    (state) => state?.Login?.userData?.emailVerification?.status
  );
  const navigate = useNavigate();
  const location = useLocation();
  const userData = getLocalStorageItem("userData");
  const role = userData?.role;
  const whitelabelData = getLocalStorageItem("whitelabelData");
  const isCorporate = whitelabelData?.groupName === "MoneyplantFX";

  const shouldRedirectToEmailVerification =
    !isCorporate && isAuthenticated && emailVerificationStatus !== 1;

  const shouldRedirectToKYC =
    !isCorporate &&
    isAuthenticated &&
    emailVerificationStatus === 1 &&
    kycStatus !== 1;

  const searchParams = new URLSearchParams(location.search);
  const referralCode = searchParams.get("ref");

  // Redirect unauthenticated users with referral code to signup
  useEffect(() => {
    if (referralCode && !isAuthenticated && location.pathname !== "/signup") {
      navigate(`/signup?ref=${referralCode}`, { replace: true });
    }
  }, [referralCode, isAuthenticated, navigate, location]);

  const HomeComponent = MainPage;

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
      {/* Modified signup route to allow access from email verification page */}
      <Route 
        path="/signup" 
        element={
          <SignupRoute />
        } 
      />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/" /> : <ForgotPassword />} />

      {/* Email Verification Route */}
      <Route
        path="/verifyemail"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" />
          ) : isCorporate ? (
            <Navigate to="/" />
          ) : emailVerificationStatus === 1 ? (
            <Navigate to="/" />
          ) : (
            <EmailVerification />
          )
        }
      />

      {/* Home Route and sub-routes (Dashboard or MainPage based on role) */}
      <Route
        path="/"
        element={
          <ProtectedRoute
            element={
              shouldRedirectToEmailVerification ? (
                <Navigate to="/verifyemail" />
              ) : shouldRedirectToKYC ? (
                <Navigate to="/completekyc" />
              ) : (
                <HomeComponent />
              )
            }
          />
        }
      >
        {/* Nested routes for MainPage tabs */}
        <Route path="accounts" element={<HomeComponent />} />
        <Route path="accounts/account-overview" element={<AccountOverview />} />
        <Route path="accounts/trading-overview" element={<TradingOverview />} />
        
        <Route path="financial/wallet" element={<HomeComponent />} />
        <Route path="financial/mt5" element={<HomeComponent />} />
        <Route path="funds-activity" element={<HomeComponent />} />
        <Route path="web-terminal" element={<HomeComponent />} />
        <Route path="transfer" element={<HomeComponent />} />
        <Route path="monitoring" element={<HomeComponent />} />
        <Route path="calendar" element={<HomeComponent />} />
        <Route path="platforms" element={<HomeComponent />} />
        <Route path="support" element={<HomeComponent />} />
        <Route path="trades-order" element={<HomeComponent />} />
        <Route path="profile" element={<HomeComponent />} />
      </Route>

      {/* Complete KYC */}
      <Route
        path="/completekyc"
        element={
          <ProtectedRoute
            element={
              isCorporate ? (
                <Navigate to="/" />
              ) : shouldRedirectToEmailVerification ? (
                <Navigate to="/verifyemail" />
              ) : kycStatus !== 1 ? (
                <CompleteKYC />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        }
      />

      {/* Common Protected Routes */}
      <Route path="/depositbank" element={<ProtectedRoute element={<DepositBank />} />} />
      <Route path="/withdrawalbank" element={<ProtectedRoute element={<WithdrawalBank />} />} />
      <Route path="/withdrawalrequest" element={<Withdrawal />} />
      <Route path="/kycauth" element={<KYCAuth />} />

      {/* Callback Routes */}
      <Route path="/payment-success" element={<NGeniusCallback />} />
      <Route path="/payment-cancel" element={<NGeniusCallback />} />
      <Route path="/crypto-success" element={<CryptoCallback />} />
      <Route path="/crypto-failed" element={<CryptoCallback />} />
      <Route path="/payment-result" element={<StripePaymentResult />} />

      {/* Catch-All Route */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};